import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Switch,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import employeeService, {
  employeeKeys,
  type EmployeePayload,
  type EmergencyContactRecord,
} from '@/services/employee.service';
import departmentService from '@/services/department.service';
import companyService from '@/services/company.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useUsers } from '@/hooks/useUsers';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';

const { Title, Text } = Typography;
const { TextArea } = Input;

const LIST_PATH = '/hr/employees';

const STEP0 = ['first_name', 'last_name', 'gender', 'date_of_birth'];
const STEP1 = ['email'];
const STEP3 = ['date_of_joining', 'employment_type', 'employment_status'];
const SUBMIT_REQUIRED_FIELDS = [...STEP0, ...STEP1, ...STEP3];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const formatDateForApi = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (dayjs.isDayjs(value)) {
    return value.format('YYYY-MM-DD');
  }
  const parsed = dayjs(value as string | number | Date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const formatReviewDate = (value: unknown): string => {
  if (!value) return '-';
  if (dayjs.isDayjs(value)) {
    return value.format('DD MMM YYYY');
  }
  const parsed = dayjs(value as string | number | Date);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : '-';
};

const formatReviewValue = (value: unknown): string => {
  if (value == null) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = String(value).trim();
  if (!text) return '-';
  // Never surface raw UUIDs in the review step
  if (UUID_RE.test(text)) return '-';
  return text;
};

type EmergencyContactFormValue = {
  id?: string;
  contact_name?: string;
  relationship?: string;
  phone_primary?: string;
  phone_secondary?: string;
  email?: string;
  address?: string;
  is_primary?: boolean;
};

const EmployeeFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [initialReviewValues, setInitialReviewValues] = useState<Record<string, unknown>>({});
  const [reviewValues, setReviewValues] = useState<Record<string, unknown>>({});
  const isEdit = Boolean(id);
  const { indiaPack, defaultPayrollCurrency } = useHrmsPolicy();

  const { data: employee, isLoading: loadingEmployee } = useQuery({
    queryKey: employeeKeys.detail(id!),
    queryFn: () => employeeService.getById(id!),
    enabled: isEdit && !!id,
  });

  const { data: emergencyContacts = [] } = useQuery({
    queryKey: employeeKeys.emergency(id!),
    queryFn: () => employeeService.listEmergencyContacts(id!),
    enabled: isEdit && !!id,
  });

  const { data: departments = [], isLoading: loadingDepartments } = useQuery({
    queryKey: ['departments', 'selection', 'employee-form'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: employeesList = [] } = useQuery({
    queryKey: employeeKeys.list(),
    queryFn: () => employeeService.list(),
    staleTime: 60_000,
  });

  const { data: users = [] } = useUsers({
    is_active: true,
    include_user_id: employee?.user_id || undefined,
  });

  const userLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      map.set(u.id, name ? `${name} (${u.email || u.username})` : u.email || u.username || u.id);
    }
    return map;
  }, [users]);

  const { data: shifts = [] } = useQuery({
    queryKey: hrmsKeys.shifts(),
    queryFn: () => hrmsService.listShifts(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: holidayCalendars = [] } = useQuery({
    queryKey: hrmsKeys.holidayCalendars(),
    queryFn: () => hrmsService.listHolidayCalendars(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: positions = [] } = useQuery({
    queryKey: hrmsKeys.positions(),
    queryFn: () => hrmsService.listPositions(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: branches = [] } = useQuery({
    queryKey: ['company-branches', 'employee-form'],
    queryFn: () => companyService.listBranches({ is_active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const reportingOptions = useMemo(
    () => employeesList.filter((e) => !isEdit || e.id !== id),
    [employeesList, isEdit, id]
  );

  const departmentLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const department of departments) {
      map.set(
        department.id,
        [department.department_code, department.department_name].filter(Boolean).join(' - ')
      );
    }
    return map;
  }, [departments]);

  const { data: workLocations = [] } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
    staleTime: 5 * 60 * 1000,
  });

  const locationLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of workLocations as any[]) {
      map.set(l.id, `${l.location_code} — ${l.location_name}`);
    }
    return map;
  }, [workLocations]);

  const branchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of branches) {
      map.set(b.id, [b.code, b.name].filter(Boolean).join(' - '));
    }
    return map;
  }, [branches]);

  const positionLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of positions as any[]) {
      map.set(p.id, `${p.position_code || ''} — ${p.position_name || p.name || ''}`.trim());
    }
    return map;
  }, [positions]);

  const employeeLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const employeeRow of employeesList) {
      map.set(
        employeeRow.id,
        [employeeRow.employee_code, `${employeeRow.first_name} ${employeeRow.last_name}`.trim()]
          .filter(Boolean)
          .join(' - ')
      );
    }
    return map;
  }, [employeesList]);

  const shiftLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of shifts) {
      map.set(s.id, s.shift_name || s.shift_code || '—');
    }
    return map;
  }, [shifts]);

  const calendarLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of holidayCalendars) {
      map.set(
        c.id,
        `${c.calendar_name || 'Calendar'}${c.calendar_year != null ? ` (${c.calendar_year})` : ''}`
      );
    }
    return map;
  }, [holidayCalendars]);

  useEffect(() => {
    if (!isEdit || !employee) return;
    let addr: { line1?: string; city?: string; country?: string } = {};
    try {
      if (employee.current_address_json) {
        addr = JSON.parse(employee.current_address_json);
      }
    } catch {
      /* ignore */
    }
    const nextValues = {
      employee_code: employee.employee_code,
      first_name: employee.first_name,
      last_name: employee.last_name,
      name_ar: employee.name_ar,
      email: employee.email,
      personal_email: employee.personal_email,
      user_id: employee.user_id || undefined,
      mobile: employee.mobile,
      home_phone: employee.home_phone,
      alternate_phone: employee.alternate_phone,
      date_of_birth: employee.date_of_birth ? dayjs(employee.date_of_birth) : undefined,
      gender: employee.gender,
      nationality: employee.nationality,
      marital_status: employee.marital_status,
      religion: employee.religion,
      blood_group: employee.blood_group,
      country_of_birth: employee.country_of_birth,
      place_of_birth: employee.place_of_birth,
      emirates_id: employee.emirates_id,
      emirates_id_expiry: employee.emirates_id_expiry ? dayjs(employee.emirates_id_expiry) : undefined,
      pan_number: employee.pan_number || undefined,
      tax_regime: employee.tax_regime || undefined,
      uan_number: employee.uan_number || undefined,
      esi_number: employee.esi_number || undefined,
      passport_number: employee.passport_number,
      passport_issue_date: employee.passport_issue_date ? dayjs(employee.passport_issue_date) : undefined,
      passport_expiry_date: employee.passport_expiry_date ? dayjs(employee.passport_expiry_date) : undefined,
      passport_place_of_issue: employee.passport_place_of_issue,
      visa_number: employee.visa_number,
      visa_type: employee.visa_type,
      visa_issue_date: employee.visa_issue_date ? dayjs(employee.visa_issue_date) : undefined,
      visa_expiry_date: employee.visa_expiry_date ? dayjs(employee.visa_expiry_date) : undefined,
      visa_sponsor: employee.visa_sponsor,
      visa_status: employee.visa_status,
      labour_card_number: employee.labour_card_number,
      labour_card_expiry: employee.labour_card_expiry ? dayjs(employee.labour_card_expiry) : undefined,
      labour_card_job_title: employee.labour_card_job_title,
      uid_number: employee.uid_number,
      work_permit_number: employee.work_permit_number,
      work_permit_issue_date: employee.work_permit_issue_date
        ? dayjs(employee.work_permit_issue_date)
        : undefined,
      work_permit_expiry_date: employee.work_permit_expiry_date
        ? dayjs(employee.work_permit_expiry_date)
        : undefined,
      insurance_provider: employee.insurance_provider,
      insurance_policy_number: employee.insurance_policy_number,
      insurance_expiry: employee.insurance_expiry ? dayjs(employee.insurance_expiry) : undefined,
      job_title: employee.job_title,
      grade: employee.grade,
      position_id: employee.position_id || undefined,
      department_id: employee.department_id || undefined,
      reporting_to_id: employee.reporting_to_id || undefined,
      branch_id: employee.branch_id || undefined,
      location_id: employee.location_id || undefined,
      cost_center_code: employee.cost_center_code,
      date_of_joining: employee.date_of_joining ? dayjs(employee.date_of_joining) : undefined,
      date_of_leaving: employee.date_of_leaving ? dayjs(employee.date_of_leaving) : undefined,
      employment_type: employee.employment_type || 'Full-time',
      employment_status: employee.employment_status || 'Active',
      contract_type: employee.contract_type,
      contract_start_date: employee.contract_start_date ? dayjs(employee.contract_start_date) : undefined,
      contract_end_date: employee.contract_end_date ? dayjs(employee.contract_end_date) : undefined,
      probation_months: employee.probation_months,
      confirmation_date: employee.confirmation_date ? dayjs(employee.confirmation_date) : undefined,
      notice_period_days: employee.notice_period_days,
      last_duty_resume_date: employee.last_duty_resume_date
        ? dayjs(employee.last_duty_resume_date)
        : undefined,
      termination_date: employee.termination_date ? dayjs(employee.termination_date) : undefined,
      termination_reason: employee.termination_reason,
      rehire_eligible: employee.rehire_eligible,
      currency: employee.currency || defaultPayrollCurrency,
      bank_name: employee.bank_name,
      bank_account_number: employee.bank_account_number,
      iban: employee.iban,
      payment_method: employee.payment_method,
      swift_code: employee.swift_code,
      bank_branch_name: employee.bank_branch_name,
      bank_branch_code: employee.bank_branch_code,
      wps_person_code: employee.wps_person_code,
      default_shift_id: employee.default_shift_id || undefined,
      holiday_calendar_id: employee.holiday_calendar_id || undefined,
      work_schedule: employee.work_schedule,
      shift_type: employee.shift_type,
      exclude_from_attendance_rules: Boolean(employee.exclude_from_attendance_rules),
      geofence_bypass_enabled: Boolean(employee.geofence_bypass_enabled),
      geofence_bypass_reason: employee.geofence_bypass_reason || undefined,
      geofence_bypass_effective_from: employee.geofence_bypass_effective_from
        ? dayjs(employee.geofence_bypass_effective_from)
        : undefined,
      geofence_bypass_effective_until: employee.geofence_bypass_effective_until
        ? dayjs(employee.geofence_bypass_effective_until)
        : undefined,
      geofence_bypass_approved_by: employee.geofence_bypass_approved_by || undefined,
      address: employee.address,
      addr_line1: addr.line1,
      addr_city: addr.city,
      addr_country: addr.country,
      emergency_contacts: emergencyContacts.map((contact) => ({
        id: contact.id,
        contact_name: contact.contact_name,
        relationship: contact.relationship,
        phone_primary: contact.phone_primary,
        phone_secondary: contact.phone_secondary,
        email: contact.email,
        address: contact.address,
        is_primary: contact.is_primary,
      })),
    };
    form.setFieldsValue(nextValues);
    setInitialReviewValues(nextValues);
    setReviewValues(nextValues);
  }, [isEdit, employee, emergencyContacts, form]);

  /** Ant Design's onValuesChange allValues only includes mounted fields; wizard unmounts other steps. */
  const syncReviewFromForm = () => {
    setReviewValues({
      ...initialReviewValues,
      ...form.getFieldsValue(true),
    });
  };

  useEffect(() => {
    if (step !== 5) return;
    setReviewValues({
      ...initialReviewValues,
      ...form.getFieldsValue(true),
    });
  }, [step, form, initialReviewValues]);

  const effectiveReviewValues =
    step === 5
      ? { ...initialReviewValues, ...form.getFieldsValue(true) }
      : { ...initialReviewValues, ...reviewValues };

  const buildPayload = (values: Record<string, unknown>): EmployeePayload => {
    const current_address_json =
      values.addr_line1 || values.addr_city || values.addr_country
        ? JSON.stringify({
            line1: values.addr_line1,
            city: values.addr_city,
            country: values.addr_country,
          })
        : undefined;

    return {
      employee_code: values.employee_code as string | undefined,
      first_name: values.first_name as string,
      last_name: values.last_name as string,
      name_ar: (values.name_ar as string) || undefined,
      email: values.email as string,
      personal_email: (values.personal_email as string) || undefined,
      user_id: (values.user_id as string) || null,
      mobile: (values.mobile as string) || undefined,
      home_phone: (values.home_phone as string) || undefined,
      alternate_phone: (values.alternate_phone as string) || undefined,
      date_of_birth: formatDateForApi(values.date_of_birth) as string,
      gender: values.gender as string,
      nationality: (values.nationality as string) || undefined,
      marital_status: (values.marital_status as string) || undefined,
      religion: (values.religion as string) || undefined,
      blood_group: (values.blood_group as string) || undefined,
      country_of_birth: (values.country_of_birth as string) || undefined,
      place_of_birth: (values.place_of_birth as string) || undefined,
      emirates_id: (values.emirates_id as string) || undefined,
      emirates_id_expiry: formatDateForApi(values.emirates_id_expiry),
      pan_number: (values.pan_number as string) || undefined,
      tax_regime: (values.tax_regime as string) || undefined,
      uan_number: (values.uan_number as string) || undefined,
      esi_number: (values.esi_number as string) || undefined,
      passport_number: (values.passport_number as string) || undefined,
      passport_issue_date: formatDateForApi(values.passport_issue_date),
      passport_expiry_date: formatDateForApi(values.passport_expiry_date),
      passport_place_of_issue: (values.passport_place_of_issue as string) || undefined,
      visa_number: (values.visa_number as string) || undefined,
      visa_type: (values.visa_type as string) || undefined,
      visa_issue_date: formatDateForApi(values.visa_issue_date),
      visa_expiry_date: formatDateForApi(values.visa_expiry_date),
      visa_sponsor: (values.visa_sponsor as string) || undefined,
      visa_status: (values.visa_status as string) || undefined,
      labour_card_number: (values.labour_card_number as string) || undefined,
      labour_card_expiry: formatDateForApi(values.labour_card_expiry),
      labour_card_job_title: (values.labour_card_job_title as string) || undefined,
      uid_number: (values.uid_number as string) || undefined,
      work_permit_number: (values.work_permit_number as string) || undefined,
      work_permit_issue_date: formatDateForApi(values.work_permit_issue_date),
      work_permit_expiry_date: formatDateForApi(values.work_permit_expiry_date),
      insurance_provider: (values.insurance_provider as string) || undefined,
      insurance_policy_number: (values.insurance_policy_number as string) || undefined,
      insurance_expiry: formatDateForApi(values.insurance_expiry),
      job_title: (values.job_title as string) || undefined,
      grade: (values.grade as string) || undefined,
      position_id: (values.position_id as string) || undefined,
      department_id: (values.department_id as string) || undefined,
      reporting_to_id: (values.reporting_to_id as string) || undefined,
      branch_id: (values.branch_id as string) || undefined,
      location_id: (values.location_id as string) || null,
      cost_center_code: (values.cost_center_code as string) || undefined,
      date_of_joining: formatDateForApi(values.date_of_joining) as string,
      date_of_leaving: formatDateForApi(values.date_of_leaving),
      employment_type: values.employment_type as string,
      employment_status: values.employment_status as string,
      contract_type: (values.contract_type as string) || undefined,
      contract_start_date: formatDateForApi(values.contract_start_date),
      contract_end_date: formatDateForApi(values.contract_end_date),
      probation_months:
        values.probation_months != null && values.probation_months !== ''
          ? Number(values.probation_months)
          : undefined,
      confirmation_date: formatDateForApi(values.confirmation_date),
      notice_period_days:
        values.notice_period_days != null && values.notice_period_days !== ''
          ? Number(values.notice_period_days)
          : undefined,
      last_duty_resume_date: formatDateForApi(values.last_duty_resume_date),
      termination_date: formatDateForApi(values.termination_date),
      termination_reason: (values.termination_reason as string) || undefined,
      rehire_eligible:
        values.rehire_eligible == null ? undefined : Boolean(values.rehire_eligible),
      currency: (values.currency as string) || defaultPayrollCurrency,
      bank_name: (values.bank_name as string) || undefined,
      bank_account_number: (values.bank_account_number as string) || undefined,
      iban: (values.iban as string) || undefined,
      payment_method: (values.payment_method as string) || undefined,
      swift_code: (values.swift_code as string) || undefined,
      bank_branch_name: (values.bank_branch_name as string) || undefined,
      bank_branch_code: (values.bank_branch_code as string) || undefined,
      wps_person_code: (values.wps_person_code as string) || undefined,
      default_shift_id: (values.default_shift_id as string) || undefined,
      holiday_calendar_id: (values.holiday_calendar_id as string) || undefined,
      work_schedule: (values.work_schedule as string) || undefined,
      shift_type: (values.shift_type as string) || undefined,
      exclude_from_attendance_rules: Boolean(values.exclude_from_attendance_rules),
      geofence_bypass_enabled: Boolean(values.geofence_bypass_enabled),
      geofence_bypass_reason: (values.geofence_bypass_reason as string) || undefined,
      geofence_bypass_effective_from: formatDateForApi(values.geofence_bypass_effective_from),
      geofence_bypass_effective_until: formatDateForApi(values.geofence_bypass_effective_until) || null,
      geofence_bypass_approved_by: (values.geofence_bypass_approved_by as string) || undefined,
      address: (values.address as string) || undefined,
      current_address_json,
    };
  };

  const stepFieldNames = (s: number): string[] => {
    if (s === 0) return STEP0;
    if (s === 1) return STEP1;
    if (s === 2) return [];
    if (s === 3) return STEP3;
    return [];
  };

  const syncEmergencyContacts = async (
    employeeId: string,
    contacts: EmergencyContactFormValue[],
    existingContacts: EmergencyContactRecord[]
  ) => {
    const cleanedContacts = contacts
      .map((contact) => ({
        id: contact.id,
        contact_name: String(contact.contact_name || '').trim(),
        relationship: String(contact.relationship || '').trim(),
        phone_primary: String(contact.phone_primary || '').trim(),
        phone_secondary: String(contact.phone_secondary || '').trim(),
        email: String(contact.email || '').trim(),
        address: String(contact.address || '').trim(),
        is_primary: Boolean(contact.is_primary),
      }))
      .filter((contact) => contact.contact_name || contact.relationship || contact.phone_primary);

    const existingIds = new Set(existingContacts.map((contact) => contact.id));
    const submittedIds = new Set(cleanedContacts.map((contact) => contact.id).filter(Boolean) as string[]);

    for (const existingContact of existingContacts) {
      if (!submittedIds.has(existingContact.id)) {
        await employeeService.deleteEmergencyContact(employeeId, existingContact.id);
      }
    }

    for (const contact of cleanedContacts) {
      if (!contact.contact_name || !contact.relationship || !contact.phone_primary) {
        continue;
      }

      const payload = {
        contact_name: contact.contact_name,
        relationship: contact.relationship,
        phone_primary: contact.phone_primary,
        phone_secondary: contact.phone_secondary || undefined,
        email: contact.email || undefined,
        address: contact.address || undefined,
        is_primary: contact.is_primary,
      };

      if (contact.id && existingIds.has(contact.id)) {
        await employeeService.updateEmergencyContact(employeeId, contact.id, payload);
      } else {
        await employeeService.createEmergencyContact(employeeId, payload);
      }
    }
  };

  const next = async () => {
    const names = stepFieldNames(step);
    if (names.length) {
      await form.validateFields(names);
    }
    syncReviewFromForm();
    setStep((x) => Math.min(x + 1, 5));
  };

  const prev = () => {
    syncReviewFromForm();
    setStep((x) => Math.max(x - 1, 0));
  };

  const onFinish = async () => {
    try {
      await form.validateFields(SUBMIT_REQUIRED_FIELDS);
      const values = form.getFieldsValue(true);
      if (!formatDateForApi(values.date_of_birth)) {
        form.setFields([{ name: 'date_of_birth', errors: ['Please select a valid date of birth'] }]);
        setStep(0);
        return;
      }
      if (!formatDateForApi(values.date_of_joining)) {
        form.setFields([{ name: 'date_of_joining', errors: ['Please select a valid date of joining'] }]);
        setStep(3);
        return;
      }
      const payload = buildPayload(values);
      if (!isEdit) {
        delete (payload as { employee_code?: string }).employee_code;
      }
      setSubmitting(true);
      if (isEdit && id) {
        const result = await employeeService.update(id, payload);
        const contacts = (values.emergency_contacts as EmergencyContactFormValue[] | undefined) || [];
        await syncEmergencyContacts(id, contacts, emergencyContacts);
        if (result && typeof result === 'object' && 'mode' in result && result.mode === 'pending_approval') {
          message.success(
            result.message ||
              'Profile change submitted for approval. Master data updates after approval.'
          );
          navigate(`/hr/employees/${id}`);
          return;
        }
        message.success('Employee updated');
      } else {
        const created = await employeeService.create(payload);
        const contacts = (values.emergency_contacts as EmergencyContactFormValue[] | undefined) || [];
        await syncEmergencyContacts(created.id, contacts, []);
        message.success(
          indiaPack
            ? 'Employee created — set pay via Salary revision and upload India statutory documents on the profile'
            : 'Employee created — set pay via Salary revision and upload UAE documents on the profile'
        );
        navigate(`/hr/employees/${created.id}`);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      if (id) {
        await queryClient.invalidateQueries({ queryKey: employeeKeys.emergency(id) });
      }
      navigate(LIST_PATH);
    } catch (e: unknown) {
      if ((e as any)?.errorFields) return;
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const apiMsg = err?.response?.data?.message;
      const apiErr = err?.response?.data?.error;
      message.error(
        (typeof apiMsg === 'string' && apiMsg.trim() ? apiMsg : null) ||
          (typeof apiErr === 'string' && apiErr.trim() ? apiErr : null) ||
          (e as Error)?.message ||
          'Could not save employee'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const pageLoading = isEdit && loadingEmployee;

  const stepItems = [
    { title: 'Personal' },
    { title: 'Contact' },
    { title: 'Emergency' },
    { title: indiaPack ? 'Job & statutory' : 'Job & UAE' },
    { title: 'Bank' },
    { title: 'Review' },
  ];

  return (
    <div style={{ padding: '0' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(LIST_PATH)} style={{ paddingLeft: 0 }}>
            Back to employees
          </Button>
          <Title level={2} style={{ marginTop: 8, marginBottom: 4 }}>
            {isEdit ? 'Propose employee profile change' : 'Add employee'}
          </Title>
          <Text type="secondary">
            {isEdit
              ? 'Changes to Active employees are submitted for approval (version control). Salary is managed via Salary revisions.'
              : indiaPack
                ? 'Create the HR master. Set compensation later with a Salary revision. Attach India statutory documents on the employee profile.'
                : 'Create the HR master. Set compensation later with a Salary revision. Attach UAE documents on the employee profile.'}
          </Text>
        </div>

        <Card>
          {pageLoading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              preserve
              onValuesChange={(_, allValues) =>
                setReviewValues((prev) => ({
                  ...initialReviewValues,
                  ...prev,
                  ...allValues,
                }))
              }
              initialValues={{
                employment_status: 'Onboarding',
                employment_type: 'Full-time',
                currency: defaultPayrollCurrency,
                tax_regime: indiaPack ? 'NEW' : undefined,
                exclude_from_attendance_rules: false,
                geofence_bypass_enabled: false,
              }}
            >
              <Steps current={step} items={stepItems} style={{ marginBottom: 24 }} />

              {isEdit && (
                <Form.Item name="employee_code" label="Employee code">
                  <Input disabled />
                </Form.Item>
              )}

              <div style={{ display: step === 0 ? 'block' : 'none' }} aria-hidden={step !== 0}>
                  <Divider orientation="left">Personal information</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="first_name" label="First name" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="last_name" label="Last name" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    {!indiaPack && (
                      <Col xs={24} md={8}>
                        <Form.Item name="name_ar" label="Name (Arabic)">
                          <Input />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
                        <Select>
                          <Select.Option value="Male">Male</Select.Option>
                          <Select.Option value="Female">Female</Select.Option>
                          <Select.Option value="Other">Other</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="date_of_birth" label="Date of birth" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="nationality" label="Nationality">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="marital_status" label="Marital status">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="religion" label="Religion">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="blood_group" label="Blood group">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="country_of_birth" label="Country of birth">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="place_of_birth" label="Place of birth">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
              </div>

              <div style={{ display: step === 1 ? 'block' : 'none' }} aria-hidden={step !== 1}>
                  <Divider orientation="left">Contact & address</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="email"
                        label="Work email"
                        rules={[{ required: true, type: 'email', message: 'Valid email required' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="personal_email" label="Personal email">
                        <Input type="email" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="user_id"
                        label="Linked login user"
                        extra="Required for My HRMS on mobile/web. Pick the ERP login for this employee (Settings → Users)."
                      >
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select login user"
                          options={users.map((u) => ({
                            value: u.id,
                            label: userLabelById.get(u.id) || u.email || u.username,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="mobile" label="Mobile">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="home_phone" label="Home phone">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="alternate_phone" label="Alternate phone">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="addr_line1" label="Current address line">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="addr_city" label="City">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="addr_country" label="Country">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="address" label="Address / notes (legacy text)">
                    <TextArea rows={2} />
                  </Form.Item>
              </div>

              <div style={{ display: step === 2 ? 'block' : 'none' }} aria-hidden={step !== 2}>
                  <Divider orientation="left">Emergency contacts</Divider>
                  <Form.List name="emergency_contacts">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Card key={key} size="small" style={{ marginBottom: 12 }}>
                            <Form.Item {...rest} name={[name, 'id']} hidden>
                              <Input />
                            </Form.Item>
                            <Row gutter={8}>
                              <Col span={8}>
                                <Form.Item {...rest} name={[name, 'contact_name']} label="Name" rules={[{ required: true }]}>
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item {...rest} name={[name, 'relationship']} label="Relationship" rules={[{ required: true }]}>
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item {...rest} name={[name, 'phone_primary']} label="Phone" rules={[{ required: true }]}>
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item {...rest} name={[name, 'phone_secondary']} label="Alt phone">
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item {...rest} name={[name, 'email']} label="Email">
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item {...rest} name={[name, 'is_primary']} label="Primary contact" valuePropName="checked">
                                  <Switch />
                                </Form.Item>
                              </Col>
                              <Col span={24}>
                                <Form.Item {...rest} name={[name, 'address']} label="Address">
                                  <TextArea rows={2} />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Button type="link" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}>
                              Remove
                            </Button>
                          </Card>
                        ))}
                        <Button type="dashed" onClick={() => add({ is_primary: false })} icon={<PlusOutlined />}>
                          Add contact
                        </Button>
                      </>
                    )}
                  </Form.List>
              </div>

              <div style={{ display: step === 3 ? 'block' : 'none' }} aria-hidden={step !== 3}>
                  <Divider orientation="left">Job & organisation</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="department_id" label="Department">
                        <Select allowClear placeholder="Department" loading={loadingDepartments} showSearch optionFilterProp="children">
                          {departments.map((d) => (
                            <Select.Option key={d.id} value={d.id}>
                              {d.department_code} — {d.department_name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="reporting_to_id" label="Reports to">
                        <Select allowClear showSearch optionFilterProp="children">
                          {reportingOptions.map((e) => (
                            <Select.Option key={e.id} value={e.id}>
                              {e.employee_code} — {e.first_name} {e.last_name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="job_title" label="Job title">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="grade" label="Grade">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="position_id" label="Position">
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          placeholder="Select position"
                        >
                          {positions.map((p: any) => (
                            <Select.Option key={p.id} value={p.id}>
                              {p.position_code} — {p.position_name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="cost_center_code" label="Cost center">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="branch_id" label="Branch">
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select branch"
                          options={branches.map((b) => ({
                            value: b.id,
                            label: `${b.code} - ${b.name}`,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="location_id"
                        label="Work location"
                        extra="Used for mobile check-in geofencing. Bulk changes: HRMS > Location assignment."
                      >
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select work location"
                          options={(workLocations as any[])
                            .filter((l) => l.is_active !== false || l.id === form.getFieldValue('location_id'))
                            .map((l) => ({
                              value: l.id,
                              label: `${l.location_code} — ${l.location_name}`,
                            }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="date_of_joining" label="Date of joining" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="employment_type" label="Employment type" rules={[{ required: true }]}>
                        <Select>
                          <Select.Option value="Full-time">Full-time</Select.Option>
                          <Select.Option value="Part-time">Part-time</Select.Option>
                          <Select.Option value="Contract">Contract</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="employment_status" label="Employment status" rules={[{ required: true }]}>
                        <Select>
                          <Select.Option value="Active">Active</Select.Option>
                          <Select.Option value="Inactive">Inactive</Select.Option>
                          <Select.Option value="Onboarding">Onboarding</Select.Option>
                          <Select.Option value="Suspended">Suspended</Select.Option>
                          <Select.Option value="On Leave">On Leave</Select.Option>
                          <Select.Option value="Resigned">Resigned</Select.Option>
                          <Select.Option value="Terminated">Terminated</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="date_of_leaving" label="Date of leaving">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="last_duty_resume_date" label="Last duty resume">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="rehire_eligible" label="Rehire eligible" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="termination_date" label="Termination date">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item name="termination_reason" label="Termination reason">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left">Contract</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="contract_type" label="Contract type">
                        <Select allowClear>
                          <Select.Option value="Unlimited">Unlimited</Select.Option>
                          <Select.Option value="Limited">Limited</Select.Option>
                          <Select.Option value="Probation">Probation</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="contract_start_date" label="Contract start">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="contract_end_date" label="Contract end">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="probation_months" label="Probation (months)">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="confirmation_date" label="Confirmation date">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="notice_period_days" label="Notice period (days)">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  {indiaPack ? (
                    <>
                      <Divider orientation="left">India statutory identity</Divider>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Required for TDS / EPF / ESI payroll engines"
                        description="PAN drives Form 16 / TDS. UAN and ESI numbers identify PF and ESIC remittances. Tax regime defaults from HRMS Setup when blank."
                      />
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="pan_number" label="PAN">
                            <Input placeholder="ABCDE1234F" maxLength={10} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="tax_regime" label="TDS tax regime">
                            <Select allowClear placeholder="Company default">
                              <Select.Option value="NEW">NEW</Select.Option>
                              <Select.Option value="OLD">OLD</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="uan_number" label="UAN (EPFO)">
                            <Input placeholder="Universal Account Number" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="esi_number" label="ESI number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item
                            name="emirates_id"
                            label="Aadhaar / National ID"
                            extra="Stored on national ID column; prefer masked Aadhaar for privacy."
                          >
                            <Input placeholder="Last 4 or masked Aadhaar" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Divider orientation="left">Passport (optional)</Divider>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_number" label="Passport number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_issue_date" label="Passport issue">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_expiry_date" label="Passport expiry">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_place_of_issue" label="Passport place of issue">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <>
                      <Divider orientation="left">Immigration & labour</Divider>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="emirates_id" label="Emirates ID">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="emirates_id_expiry" label="Emirates ID expiry">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="uid_number" label="UID number">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_number" label="Passport number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_issue_date" label="Passport issue">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_expiry_date" label="Passport expiry">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="passport_place_of_issue" label="Passport place of issue">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="visa_number" label="Visa number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="visa_type" label="Visa type">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="visa_issue_date" label="Visa issue">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="visa_expiry_date" label="Visa expiry">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="visa_status" label="Visa status">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item name="visa_sponsor" label="Visa sponsor">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="labour_card_number" label="Labour card number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="labour_card_expiry" label="Labour card expiry">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="labour_card_job_title" label="Labour card job title">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="work_permit_number" label="Work permit number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="work_permit_issue_date" label="Work permit issue">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="work_permit_expiry_date" label="Work permit expiry">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}

                  <Divider orientation="left">Insurance</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="insurance_provider" label="Insurance provider">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="insurance_policy_number" label="Policy number">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="insurance_expiry" label="Insurance expiry">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
              </div>

              <div style={{ display: step === 4 ? 'block' : 'none' }} aria-hidden={step !== 4}>
                  <Divider orientation="left">
                    {indiaPack ? 'Bank & payment (NEFT)' : 'Bank & payment (WPS)'}
                  </Divider>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Salary is not entered here"
                    description={
                      isEdit ? (
                        <span>
                          Current basic is read-only on the employee profile. Use{' '}
                          <a onClick={() => navigate('/hr/salary-revisions/new')}>Salary revisions</a> to change pay.
                          {indiaPack ? ' NEFT export needs account number + IFSC.' : ''}
                        </span>
                      ) : (
                        <span>
                          After creating the employee, open their profile and create a Salary revision to set basic and
                          allowances.
                          {indiaPack ? ' For salary NEFT, enter account number and IFSC below.' : ''}
                        </span>
                      )
                    }
                  />
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="currency" label="Payroll currency" rules={[{ required: true }]}>
                        <Select>
                          {indiaPack ? (
                            <>
                              <Select.Option value="INR">INR</Select.Option>
                              <Select.Option value="USD">USD</Select.Option>
                              <Select.Option value="AED">AED</Select.Option>
                            </>
                          ) : (
                            <>
                              <Select.Option value="AED">AED</Select.Option>
                              <Select.Option value="USD">USD</Select.Option>
                              <Select.Option value="SAR">SAR</Select.Option>
                              <Select.Option value="INR">INR</Select.Option>
                            </>
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                    {!indiaPack && (
                      <Col xs={24} md={12}>
                        <Form.Item name="wps_person_code" label="WPS person code">
                          <Input />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="bank_name" label="Bank name">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="bank_account_number" label="Account number">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="iban" label={indiaPack ? 'IBAN (optional)' : 'IBAN'}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="payment_method" label="Payment method">
                        <Select allowClear placeholder={indiaPack ? 'BankTransfer / NEFT' : 'Bank transfer, WPS…'}>
                          <Select.Option value="BankTransfer">BankTransfer</Select.Option>
                          <Select.Option value="Cash">Cash</Select.Option>
                          <Select.Option value="Cheque">Cheque</Select.Option>
                          {!indiaPack && <Select.Option value="WPS">WPS</Select.Option>}
                          {indiaPack && <Select.Option value="NEFT">NEFT</Select.Option>}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="swift_code" label="SWIFT">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="bank_branch_name" label="Bank branch">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="bank_branch_code"
                        label={indiaPack ? 'IFSC' : 'Bank branch code'}
                        extra={indiaPack ? 'Mandatory for payroll NEFT CSV export' : undefined}
                      >
                        <Input placeholder={indiaPack ? 'e.g. SBIN0001234' : undefined} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left">Schedule</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="default_shift_id" label="Default shift">
                        <Select allowClear showSearch optionFilterProp="children" placeholder="Select shift">
                          {shifts.map((s: any) => (
                            <Select.Option key={s.id} value={s.id}>
                              {s.shift_name || s.shift_code || 'Shift'}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="holiday_calendar_id" label="Holiday calendar">
                        <Select allowClear showSearch optionFilterProp="children" placeholder="Select calendar">
                          {holidayCalendars.map((c: any) => (
                            <Select.Option key={c.id} value={c.id}>
                              {c.calendar_name}
                              {c.calendar_year != null ? ` (${c.calendar_year})` : ''}
                              {c.is_default ? ' · default' : ''}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="work_schedule" label="Work schedule">
                        <Input placeholder="e.g. Sun–Thu 08:00–17:00" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item name="shift_type" label="Shift type">
                        <Select allowClear>
                          <Select.Option value="Day">Day</Select.Option>
                          <Select.Option value="Night">Night</Select.Option>
                          <Select.Option value="Rotating">Rotating</Select.Option>
                          <Select.Option value="Flexible">Flexible</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="exclude_from_attendance_rules"
                        label="Exclude from attendance rules"
                        valuePropName="checked"
                        extra="Timesheet / attendance rule exclusion — not a geofence exemption."
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left">Mobile check-in — geofence</Divider>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) =>
                      prev.geofence_bypass_enabled !== cur.geofence_bypass_enabled
                    }
                  >
                    {() => {
                      const bypassOn = Boolean(form.getFieldValue('geofence_bypass_enabled'));
                      return (
                        <>
                          <Row gutter={16}>
                            <Col xs={24} md={8}>
                              <Form.Item
                                name="geofence_bypass_enabled"
                                label="Bypass Geofence Validation"
                                valuePropName="checked"
                                extra="Exempts this employee from mandatory geofence checks on manual mobile check-in only. Does not enable automatic check-in outside a site."
                              >
                                <Switch />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                              <Form.Item
                                name="geofence_bypass_effective_from"
                                label="Effective from"
                                rules={
                                  bypassOn
                                    ? [{ required: true, message: 'Required when bypass is enabled' }]
                                    : undefined
                                }
                              >
                                <DatePicker style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                              <Form.Item
                                name="geofence_bypass_effective_until"
                                label="Effective until"
                                extra="Optional expiry. Leave blank for open-ended."
                              >
                                <DatePicker style={{ width: '100%' }} allowClear />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={24} md={16}>
                              <Form.Item
                                name="geofence_bypass_reason"
                                label="Exemption reason"
                                rules={
                                  bypassOn
                                    ? [
                                        {
                                          required: true,
                                          min: 5,
                                          message: 'Business justification required (min 5 characters)',
                                        },
                                      ]
                                    : undefined
                                }
                              >
                                <TextArea
                                  rows={2}
                                  placeholder="e.g. Field engineer covering multiple client sites this quarter"
                                  disabled={!bypassOn && !form.getFieldValue('geofence_bypass_reason')}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      );
                    }}
                  </Form.Item>
              </div>

              {step === 5 && (
                <>
                  <Divider orientation="left">Review</Divider>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Review the entered details below and confirm everything looks correct before submitting.
                  </Text>

                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Card size="small" title="Personal information">
                      <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="First name">{formatReviewValue(effectiveReviewValues.first_name)}</Descriptions.Item>
                        <Descriptions.Item label="Last name">{formatReviewValue(effectiveReviewValues.last_name)}</Descriptions.Item>
                        {!indiaPack && (
                          <Descriptions.Item label="Arabic name">{formatReviewValue(effectiveReviewValues.name_ar)}</Descriptions.Item>
                        )}
                        <Descriptions.Item label="Gender">{formatReviewValue(effectiveReviewValues.gender)}</Descriptions.Item>
                        <Descriptions.Item label="Date of birth">
                          {formatReviewDate(effectiveReviewValues.date_of_birth)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Nationality">{formatReviewValue(effectiveReviewValues.nationality)}</Descriptions.Item>
                        <Descriptions.Item label="Marital status">{formatReviewValue(effectiveReviewValues.marital_status)}</Descriptions.Item>
                        <Descriptions.Item label="Religion">{formatReviewValue(effectiveReviewValues.religion)}</Descriptions.Item>
                        <Descriptions.Item label="Blood group">{formatReviewValue(effectiveReviewValues.blood_group)}</Descriptions.Item>
                        <Descriptions.Item label="Country of birth">{formatReviewValue(effectiveReviewValues.country_of_birth)}</Descriptions.Item>
                        <Descriptions.Item label="Place of birth" span={2}>{formatReviewValue(effectiveReviewValues.place_of_birth)}</Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title="Contact and address">
                      <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="Work email">{formatReviewValue(effectiveReviewValues.email)}</Descriptions.Item>
                        <Descriptions.Item label="Personal email">{formatReviewValue(effectiveReviewValues.personal_email)}</Descriptions.Item>
                        <Descriptions.Item label="Linked login user" span={2}>
                          {effectiveReviewValues.user_id
                            ? userLabelById.get(String(effectiveReviewValues.user_id)) ||
                              String(effectiveReviewValues.user_id)
                            : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mobile">{formatReviewValue(effectiveReviewValues.mobile)}</Descriptions.Item>
                        <Descriptions.Item label="Home phone">{formatReviewValue(effectiveReviewValues.home_phone)}</Descriptions.Item>
                        <Descriptions.Item label="Alternate phone">{formatReviewValue(effectiveReviewValues.alternate_phone)}</Descriptions.Item>
                        <Descriptions.Item label="Address line">{formatReviewValue(effectiveReviewValues.addr_line1)}</Descriptions.Item>
                        <Descriptions.Item label="City">{formatReviewValue(effectiveReviewValues.addr_city)}</Descriptions.Item>
                        <Descriptions.Item label="Country">{formatReviewValue(effectiveReviewValues.addr_country)}</Descriptions.Item>
                        <Descriptions.Item label="Address / notes" span={2}>{formatReviewValue(effectiveReviewValues.address)}</Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title="Emergency contacts">
                      {((effectiveReviewValues.emergency_contacts as EmergencyContactFormValue[] | undefined) || []).length > 0 ? (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {((effectiveReviewValues.emergency_contacts as EmergencyContactFormValue[] | undefined) || []).map((contact, index) => (
                            <Card key={contact.id || index} size="small" type="inner" title={`Contact ${index + 1}`}>
                              <Descriptions column={2} size="small" bordered>
                                <Descriptions.Item label="Name">{formatReviewValue(contact.contact_name)}</Descriptions.Item>
                                <Descriptions.Item label="Relationship">{formatReviewValue(contact.relationship)}</Descriptions.Item>
                                <Descriptions.Item label="Primary phone">{formatReviewValue(contact.phone_primary)}</Descriptions.Item>
                                <Descriptions.Item label="Secondary phone">{formatReviewValue(contact.phone_secondary)}</Descriptions.Item>
                                <Descriptions.Item label="Email">{formatReviewValue(contact.email)}</Descriptions.Item>
                                <Descriptions.Item label="Primary contact">{contact.is_primary ? 'Yes' : 'No'}</Descriptions.Item>
                                <Descriptions.Item label="Address" span={2}>{formatReviewValue(contact.address)}</Descriptions.Item>
                              </Descriptions>
                            </Card>
                          ))}
                        </Space>
                      ) : (
                        <Text type="secondary">No emergency contacts added.</Text>
                      )}
                    </Card>

                    <Card size="small" title="Employment and contract">
                      <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="Department">
                          {departmentLabelById.get(String(effectiveReviewValues.department_id || '')) || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Reporting to">
                          {employeeLabelById.get(String(effectiveReviewValues.reporting_to_id || '')) || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Job title">{formatReviewValue(effectiveReviewValues.job_title)}</Descriptions.Item>
                        <Descriptions.Item label="Grade">{formatReviewValue(effectiveReviewValues.grade)}</Descriptions.Item>
                        <Descriptions.Item label="Position">
                          {positionLabelById.get(String(effectiveReviewValues.position_id || '')) || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Cost center">{formatReviewValue(effectiveReviewValues.cost_center_code)}</Descriptions.Item>
                        <Descriptions.Item label="Branch">
                          {branchLabelById.get(String(effectiveReviewValues.branch_id || '')) || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Work location">
                          {locationLabelById.get(String(effectiveReviewValues.location_id || '')) || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Date of joining">{formatReviewDate(effectiveReviewValues.date_of_joining)}</Descriptions.Item>
                        <Descriptions.Item label="Date of leaving">{formatReviewDate(effectiveReviewValues.date_of_leaving)}</Descriptions.Item>
                        <Descriptions.Item label="Employment type">{formatReviewValue(effectiveReviewValues.employment_type)}</Descriptions.Item>
                        <Descriptions.Item label="Employment status">{formatReviewValue(effectiveReviewValues.employment_status)}</Descriptions.Item>
                        <Descriptions.Item label="Contract type">{formatReviewValue(effectiveReviewValues.contract_type)}</Descriptions.Item>
                        <Descriptions.Item label="Contract start">{formatReviewDate(effectiveReviewValues.contract_start_date)}</Descriptions.Item>
                        <Descriptions.Item label="Contract end">{formatReviewDate(effectiveReviewValues.contract_end_date)}</Descriptions.Item>
                        <Descriptions.Item label="Probation months">{formatReviewValue(effectiveReviewValues.probation_months)}</Descriptions.Item>
                        <Descriptions.Item label="Confirmation">{formatReviewDate(effectiveReviewValues.confirmation_date)}</Descriptions.Item>
                        <Descriptions.Item label="Notice period days">{formatReviewValue(effectiveReviewValues.notice_period_days)}</Descriptions.Item>
                        <Descriptions.Item label="Termination date">{formatReviewDate(effectiveReviewValues.termination_date)}</Descriptions.Item>
                        <Descriptions.Item label="Rehire eligible">{formatReviewValue(effectiveReviewValues.rehire_eligible)}</Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title={indiaPack ? 'India statutory & passport' : 'Immigration and labour'}>
                      <Descriptions column={2} size="small" bordered>
                        {indiaPack ? (
                          <>
                            <Descriptions.Item label="PAN">{formatReviewValue(effectiveReviewValues.pan_number)}</Descriptions.Item>
                            <Descriptions.Item label="Tax regime">{formatReviewValue(effectiveReviewValues.tax_regime)}</Descriptions.Item>
                            <Descriptions.Item label="UAN">{formatReviewValue(effectiveReviewValues.uan_number)}</Descriptions.Item>
                            <Descriptions.Item label="ESI number">{formatReviewValue(effectiveReviewValues.esi_number)}</Descriptions.Item>
                            <Descriptions.Item label="Aadhaar / National ID">{formatReviewValue(effectiveReviewValues.emirates_id)}</Descriptions.Item>
                            <Descriptions.Item label="Passport number">{formatReviewValue(effectiveReviewValues.passport_number)}</Descriptions.Item>
                            <Descriptions.Item label="Passport issue">{formatReviewDate(effectiveReviewValues.passport_issue_date)}</Descriptions.Item>
                            <Descriptions.Item label="Passport expiry">{formatReviewDate(effectiveReviewValues.passport_expiry_date)}</Descriptions.Item>
                          </>
                        ) : (
                          <>
                            <Descriptions.Item label="Emirates ID">{formatReviewValue(effectiveReviewValues.emirates_id)}</Descriptions.Item>
                            <Descriptions.Item label="Emirates ID expiry">{formatReviewDate(effectiveReviewValues.emirates_id_expiry)}</Descriptions.Item>
                            <Descriptions.Item label="Passport number">{formatReviewValue(effectiveReviewValues.passport_number)}</Descriptions.Item>
                            <Descriptions.Item label="Passport issue">{formatReviewDate(effectiveReviewValues.passport_issue_date)}</Descriptions.Item>
                            <Descriptions.Item label="Passport expiry">{formatReviewDate(effectiveReviewValues.passport_expiry_date)}</Descriptions.Item>
                            <Descriptions.Item label="Place of issue">{formatReviewValue(effectiveReviewValues.passport_place_of_issue)}</Descriptions.Item>
                            <Descriptions.Item label="Visa number">{formatReviewValue(effectiveReviewValues.visa_number)}</Descriptions.Item>
                            <Descriptions.Item label="Visa type">{formatReviewValue(effectiveReviewValues.visa_type)}</Descriptions.Item>
                            <Descriptions.Item label="Visa issue">{formatReviewDate(effectiveReviewValues.visa_issue_date)}</Descriptions.Item>
                            <Descriptions.Item label="Visa expiry">{formatReviewDate(effectiveReviewValues.visa_expiry_date)}</Descriptions.Item>
                            <Descriptions.Item label="Visa sponsor">{formatReviewValue(effectiveReviewValues.visa_sponsor)}</Descriptions.Item>
                            <Descriptions.Item label="Visa status">{formatReviewValue(effectiveReviewValues.visa_status)}</Descriptions.Item>
                            <Descriptions.Item label="Labour card #">{formatReviewValue(effectiveReviewValues.labour_card_number)}</Descriptions.Item>
                            <Descriptions.Item label="Labour card expiry">{formatReviewDate(effectiveReviewValues.labour_card_expiry)}</Descriptions.Item>
                            <Descriptions.Item label="UID">{formatReviewValue(effectiveReviewValues.uid_number)}</Descriptions.Item>
                            <Descriptions.Item label="Work permit #">{formatReviewValue(effectiveReviewValues.work_permit_number)}</Descriptions.Item>
                            <Descriptions.Item label="Work permit issue">{formatReviewDate(effectiveReviewValues.work_permit_issue_date)}</Descriptions.Item>
                            <Descriptions.Item label="Work permit expiry">{formatReviewDate(effectiveReviewValues.work_permit_expiry_date)}</Descriptions.Item>
                          </>
                        )}
                        <Descriptions.Item label="Insurance provider">{formatReviewValue(effectiveReviewValues.insurance_provider)}</Descriptions.Item>
                        <Descriptions.Item label="Policy number">{formatReviewValue(effectiveReviewValues.insurance_policy_number)}</Descriptions.Item>
                        <Descriptions.Item label="Insurance expiry" span={2}>{formatReviewDate(effectiveReviewValues.insurance_expiry)}</Descriptions.Item>
                      </Descriptions>
                    </Card>

                    <Card size="small" title={indiaPack ? 'Bank, NEFT and schedule' : 'Bank, WPS and schedule'}>
                      <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="Currency">{formatReviewValue(effectiveReviewValues.currency)}</Descriptions.Item>
                        {!indiaPack && (
                          <Descriptions.Item label="WPS person code">{formatReviewValue(effectiveReviewValues.wps_person_code)}</Descriptions.Item>
                        )}
                        <Descriptions.Item label="Bank name">{formatReviewValue(effectiveReviewValues.bank_name)}</Descriptions.Item>
                        <Descriptions.Item label="Account number">{formatReviewValue(effectiveReviewValues.bank_account_number)}</Descriptions.Item>
                        <Descriptions.Item label="IBAN">{formatReviewValue(effectiveReviewValues.iban)}</Descriptions.Item>
                        <Descriptions.Item label="Payment method">{formatReviewValue(effectiveReviewValues.payment_method)}</Descriptions.Item>
                        <Descriptions.Item label="SWIFT">{formatReviewValue(effectiveReviewValues.swift_code)}</Descriptions.Item>
                        <Descriptions.Item label="Bank branch">{formatReviewValue(effectiveReviewValues.bank_branch_name)}</Descriptions.Item>
                        <Descriptions.Item label={indiaPack ? 'IFSC' : 'Branch code'}>
                          {formatReviewValue(effectiveReviewValues.bank_branch_code)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Default shift">
                          {shiftLabelById.get(String(effectiveReviewValues.default_shift_id || '')) || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Holiday calendar">
                          {calendarLabelById.get(String(effectiveReviewValues.holiday_calendar_id || '')) || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Work schedule">{formatReviewValue(effectiveReviewValues.work_schedule)}</Descriptions.Item>
                        <Descriptions.Item label="Bypass geofence">
                          {formatReviewValue(effectiveReviewValues.geofence_bypass_enabled)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Geofence exemption reason">
                          {formatReviewValue(effectiveReviewValues.geofence_bypass_reason)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Exemption from">
                          {formatReviewDate(effectiveReviewValues.geofence_bypass_effective_from)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Exemption until">
                          {formatReviewDate(effectiveReviewValues.geofence_bypass_effective_until)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Shift type">{formatReviewValue(effectiveReviewValues.shift_type)}</Descriptions.Item>
                        <Descriptions.Item label="Exclude attendance rules">
                          {formatReviewValue(effectiveReviewValues.exclude_from_attendance_rules)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Space>
                </>
              )}

              <Divider />
              <Space>
                {step > 0 && <Button onClick={prev}>Previous</Button>}
                {step < 5 && (
                  <Button type="primary" onClick={next}>
                    Next
                  </Button>
                )}
                {step === 5 && (
                  <Button type="primary" icon={<SaveOutlined />} loading={submitting} onClick={onFinish}>
                    Submit
                  </Button>
                )}
                <Button onClick={() => navigate(LIST_PATH)}>Cancel</Button>
              </Space>
            </Form>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default EmployeeFormPage;
